import React, { useState } from "react";

const MultiBusinessTotalsTable = ({ selectedBusinesses = [], compiledData = [], onShowDetails }) => {
	const normalize = (s) =>
		String(s || "")
			.trim()
			.toLowerCase();
	const selectedSet = new Set(selectedBusinesses.map(normalize));

	const [expandedBusinesses, setExpandedBusinesses] = useState({});
	const [expandedCategories, setExpandedCategories] = useState({});
	const [allExpanded, setAllExpanded] = useState(false);

	// Filter and de-duplicate by normalized business name to avoid duplicate rows
	const matched = (() => {
		const seen = new Set();
		const result = [];
		for (const b of compiledData) {
			const key = normalize(b.business);
			if (selectedSet.has(key) && !seen.has(key)) {
				seen.add(key);
				result.push(b);
			}
		}
		return result;
	})();
	if (matched.length === 0) return null;

	// Determine months length from first available category
	const monthsLength = matched.find((b) => b.categories && b.categories[0] && Array.isArray(b.categories[0].monthlyTotals))?.categories[0].monthlyTotals.length || 12;

	// Per-business monthly totals and overall combined
	const perBusinessMonthlyTotals = matched.map((b) => {
		const totals = Array(monthsLength).fill(0);
		b.categories.forEach((cat) => {
			cat.monthlyTotals.forEach((amt, idx) => {
				totals[idx] += amt;
			});
		});
		const total = totals.reduce((sum, val) => sum + val, 0);
		const denominator = b?.monthsInRange || monthsLength || 12;
		const average = total / denominator;
		return { name: b.business, months: totals, total, average, denominator };
	});

	// Aggregate monthly totals across all selected businesses and all categories
	const aggregatedMonthlyTotals = Array(monthsLength).fill(0);
	matched.forEach((b) => {
		b.categories.forEach((cat) => {
			cat.monthlyTotals.forEach((amt, idx) => {
				aggregatedMonthlyTotals[idx] += amt;
			});
		});
	});

	const grandTotal = aggregatedMonthlyTotals.reduce((sum, val) => sum + val, 0);
	const monthsDenominator = matched[0]?.monthsInRange || monthsLength || 12;
	const grandAverage = grandTotal / monthsDenominator;

	const monthLabels = Array.from({ length: monthsLength }, (_, idx) => {
		const date = new Date(2024, idx);
		return date.toLocaleString("en-US", { month: "short" });
	});

	const toggleBusiness = (businessName) => {
		setExpandedBusinesses((prev) => ({ ...prev, [businessName]: !prev[businessName] }));
	};

	const toggleCategory = (businessName, categoryName) => {
		const key = `${businessName}::${categoryName}`;
		setExpandedCategories((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	const handleExpandCollapseAll = () => {
		const newStateBusinesses = {};
		const newStateCategories = {};
		matched.forEach((b) => {
			newStateBusinesses[b.business] = !allExpanded;
			b.categories.forEach((cat) => {
				if (cat.subcategories && cat.subcategories.length > 0) {
					const key = `${b.business}::${cat.category}`;
					newStateCategories[key] = !allExpanded;
				}
			});
		});
		setExpandedBusinesses(newStateBusinesses);
		setExpandedCategories(newStateCategories);
		setAllExpanded(!allExpanded);
	};

	const formatCurrency = (val) => Math.round(val).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 });

	return (
		<div className="v4-spending-table__scroll">
			<table className="v4-spending-table">
				<thead>
					<tr className="v4-spending-table__header-row">
						<th className="v4-spending-table__header-cell v4-spending-table__header-cell--sticky">
							<button className="v4-spending-table__expand-all" onClick={handleExpandCollapseAll}>
								{allExpanded ? "−" : "+"}
							</button>
						</th>
						{monthLabels.map((label, idx) => (
							<th className="v4-spending-table__header-cell" key={idx}>
								{label}
							</th>
						))}
						<th className="v4-spending-table__header-cell v4-spending-table__header-cell--total">Total</th>
						<th className="v4-spending-table__header-cell v4-spending-table__header-cell--avg">Avg</th>
					</tr>
				</thead>
				<tbody>
					{matched.map((business, bIdx) => {
						const perBiz = perBusinessMonthlyTotals[bIdx];
						const isBizExpanded = expandedBusinesses[business.business] || false;
						const hasAnySubcats = business.categories.some((cat) => cat.subcategories && cat.subcategories.length > 0);

						return (
							<React.Fragment key={business.business}>
								{/* Business Row */}
								<tr className="v4-spending-table__row--category">
									<td className="v4-spending-table__sticky-col" onClick={() => hasAnySubcats && toggleBusiness(business.business)}>
										{business.business}
										{hasAnySubcats && <span className="v4-spending-table__expand-toggle">{isBizExpanded ? "−" : "+"}</span>}
									</td>
									{perBiz.months.map((val, idx) => (
										<td className={`v4-spending-table__cell ${val === 0 ? "v4-spending-table__cell--zero" : ""}`} key={idx}>
											{formatCurrency(val)}
										</td>
									))}
									<td className="v4-spending-table__cell v4-spending-table__cell--total">{formatCurrency(perBiz.total)}</td>
									<td className="v4-spending-table__cell v4-spending-table__cell--avg">{formatCurrency(perBiz.average)}</td>
								</tr>

								{/* Categories within Business */}
								{business.categories.map((cat) => {
									const hasSubcategories = cat.subcategories && cat.subcategories.length > 0;
									const key = `${business.business}::${cat.category}`;
									const isCatExpanded = expandedCategories[key] || false;

									return (
										<React.Fragment key={key}>
											<tr className={`v4-spending-table__row--subcategory ${isBizExpanded ? "expanded" : "collapsed"}`}>
												<td className="v4-spending-table__sticky-col" onClick={() => hasSubcategories && toggleCategory(business.business, cat.category)}>
													{cat.category}
													{hasSubcategories && <span className="v4-spending-table__expand-toggle">{isCatExpanded ? "−" : "+"}</span>}
												</td>
												{cat.monthlyTotals.map((val, idx) => (
													<td className={`v4-spending-table__cell ${val === 0 ? "v4-spending-table__cell--zero" : ""}`} key={idx}>
														{formatCurrency(val)}
													</td>
												))}
												<td className="v4-spending-table__cell v4-spending-table__cell--total">{formatCurrency(cat.total)}</td>
												<td className="v4-spending-table__cell v4-spending-table__cell--avg">{formatCurrency(cat.monthlyAverage)}</td>
											</tr>

											{/* Subcategories */}
											{hasSubcategories &&
												cat.subcategories.map((subcat) => (
													<tr key={`${key}::${subcat.category}`} className={`v4-spending-table__row--subcategory ${isCatExpanded ? "expanded" : "collapsed"}`}>
														<td className="v4-spending-table__sticky-col" style={{ paddingLeft: 48 }}>{subcat.category}</td>
														{subcat.monthlyTotals.map((val, idx) => (
															<td className={`v4-spending-table__cell ${val === 0 ? "v4-spending-table__cell--zero" : ""}`} key={idx}>
																{formatCurrency(val)}
															</td>
														))}
														<td className="v4-spending-table__cell v4-spending-table__cell--total">{formatCurrency(subcat.total)}</td>
														<td className="v4-spending-table__cell v4-spending-table__cell--avg">{formatCurrency(subcat.monthlyAverage)}</td>
													</tr>
												))}
										</React.Fragment>
									);
								})}
							</React.Fragment>
						);
					})}

					{/* Footer Row */}
					<tr className="v4-spending-table__row--footer">
						<td className="v4-spending-table__sticky-col">Combined Total</td>
						{aggregatedMonthlyTotals.map((val, idx) => (
							<td className={`v4-spending-table__cell ${val === 0 ? "v4-spending-table__cell--zero" : ""}`} key={idx}>
								{formatCurrency(val)}
							</td>
						))}
						<td className="v4-spending-table__cell v4-spending-table__cell--total">{formatCurrency(grandTotal)}</td>
						<td className="v4-spending-table__cell v4-spending-table__cell--avg">{formatCurrency(grandAverage)}</td>
					</tr>
				</tbody>
			</table>
		</div>
	);
};

export default MultiBusinessTotalsTable;
